import {appConfig} from "../config";
import {WorkTimeModel} from "./WorkTimeModelApi";
import {apiRequest} from "../util/ApiUtils";
import {AttributeDefinition} from "./AttributeDefinitionApi";
import {UserAccount} from "./UserAccountApi";
import {ApiResponse} from "./types";
import {ApiClient} from "./apiClient";
import {BusinessActionDefinition} from "./BusinessActionDefinitionApi";
import {BusinessAction} from "./BusinessActionApi";

export interface Employee {

    employeeId: string;
    employeeCode: string;
    manager: Employee;
    workTimeModel: WorkTimeModel;

    // Use direct fields for system attributes
    firstname: string;
    lastname: string;
    preferredName: string;
    avatar: string;
    status: string;
    gender: string;
    email: string;
    dateOfBirth: string;
    entryDate: string;
    exitDate: string;
    endOfProbation: string;
    probationPeriod: string;
    employmentType: string;
    jobType: string;
    weeklyWorkingHours: number;
    attributeValues: AttributeValue[]
    customFields: Record<string, any>;
}

export interface AttributeValue {
    attributeDefinition: AttributeDefinition;
    value: any;
    displayValue: any;
    attributeValue: any;
}

export interface AttributeUpdate {
    name: string;
    value: any;
    validFrom?: string;
}

export interface EmployeeHistory {
    id: number;
    attributeName: string;
    attributeDefinition: AttributeDefinition;
    oldValue: string;
    newValue: string
    effectiveDate: string;
    modifier: UserAccount;
    createdAt: string;
}

export enum FilterOperator {
    EQUALS = 'EQUALS',
    NOT_EQUALS = 'NOT_EQUALS',
    CONTAINS = 'CONTAINS',
    NOT_CONTAINS = 'NOT_CONTAINS',
    GREATER_THAN = 'GREATER_THAN',
    LESS_THAN = 'LESS_THAN',
    GREATER_THAN_OR_EQUAL = 'GREATER_THAN_OR_EQUAL',
    LESS_THAN_OR_EQUAL = 'LESS_THAN_OR_EQUAL',
    IN = 'IN',
    NOT_IN = 'NOT_IN',
    IS_NULL = 'IS_NULL',
    IS_NOT_NULL = 'IS_NOT_NULL',
    BETWEEN = 'BETWEEN',
    IN_NEXT_N_DAYS = 'IN_NEXT_N_DAYS',
    IN_PAST_N_DAYS = 'IN_PAST_N_DAYS',
    IN_NEXT_N_WEEKS = 'IN_NEXT_N_WEEKS',
    IN_PAST_N_WEEKS = 'IN_PAST_N_WEEKS',
    IN_NEXT_N_MONTHS = 'IN_NEXT_N_MONTHS',
    IN_PAST_N_MONTHS = 'IN_PAST_N_MONTHS',
    THIS_WEEK = 'THIS_WEEK',
    THIS_MONTH = 'THIS_MONTH',
    THIS_YEAR = 'THIS_YEAR',
}

export interface AttributeFilter {
    attributeName: string;
    operator: FilterOperator;
    value?: any;
    from?: string;
    to?: string;
}

// TODO: Integrate selected columns to filter to required data only to reduce bandwidth
export interface EmployeeSearchRequest {
    query?: string;
    page: number | undefined,
    sortBy?: string,
    asc?: boolean,
    attributeFilters?: AttributeFilter[],
    limit?: number
}

export interface EmployeeSearchResponse {
    employees: Employee[];
    page: number,
    size: number,
    totalElements: number,
    totalPages: number
}

export interface CreateEmployeeRequest {
    attributes: { [key: string]: any };
    createUserAccount: boolean;
    userRoleIds: number[];
    sendInvitation: boolean;
}

export interface UpdateEmployeesRequest {
    employeeIds: string[];
    branchId?: number;
    departmentId?: number;
    teamId?: number;
}

export interface DeleteEmployeeRequest {
    employeeId: string;
}

export interface EmployeeUserAccountCreateRequest {
    username: string;
    userRoleIds: number[];
    sendInvitation: boolean;
}

export interface EmployeeEventRequest {
    businessEventType: string;
    attributeUpdates: AttributeUpdate[];
}

export interface AnnualLeaveBalance {
    year: number;
    employee: string;
    totalVacationDays: number;
    remainingVacationDays: number;
    plannedVacationDays: number;
    usedVacationDays: number;
    carriedOverVacationDays: number;
    expiredVacationDays: number;
}

export interface WorkTimeBalance {
    displayName: string;
    periodStart: string;
    periodEnd: string;
    targetMinutes: string;
    targetHours: string;
    actualMinutes: string;
    actualHours: string;
    workTimePolicy: WorkTimePolicy;
}

export interface WorkTimePolicy {
    name: string;
}

export interface WorkTimeBalanceSummary {
    totalTargetMinutes: string;
    totalActualMinutes: string;
    totalBalanceMinutes: string;
    totalOvertimeMinutes: string;

    totalTargetHours: string;
    totalActualHours: string;
    totalOvertimeHours: string;
    totalIncludedOvertimeHours: string;
    totalPayableOvertimeHours: string;
    totalDeficitHours: string;

    totalIncludedOvertimeMinutes: string;
    totalPayableOvertimeMinutes: string;
    totalDeficitMinutes: string;
    allTargetsMet: boolean;
    accountsWithDeficit: string[];
    accountsWithOvertime: string[];
}

export type RetrieveEmployeeHistoryCallback = (arg: EmployeeHistory[]) => void;

export type DeleteEmployeeCallback = (arg: string) => void;

export type UpdateEmployeesResponseCallback = (arg: Employee[]) => void;

const backendUrl = appConfig.backendUrl;

const apiClient = new ApiClient(`${appConfig.backendUrl}/api/employees`);

export async function employeeSearch(request: EmployeeSearchRequest): Promise<EmployeeSearchResponse> {

    const data = await apiRequest(`${backendUrl}/api/employees/search`, {
        method: "POST",
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            query: request.query,
            page: request.page,
            sortBy: request.sortBy,
            asc: request.asc,
            attributeFilters: request.attributeFilters
        })
    });

    return data as EmployeeSearchResponse;
}

export async function retrieveEmployee(employeeId: string): Promise<Employee> {
    const data = await apiRequest(`${backendUrl}/api/employees/${employeeId}`, {
        method: "GET",
    });

    return data as Employee;
}

export async function retrieveEmployeeHistory(employeeId: string, callback: RetrieveEmployeeHistoryCallback): Promise<void> {
    try {
        const data = await apiRequest(`${backendUrl}/api/employees/${employeeId}/history`, {
            method: "GET",
        });

        if (data) {
            callback(data);
        }

    } catch (error) {
        console.error('There was a problem with the fetch operation:', error);
    }
}

export async function createEmployee(requestData: CreateEmployeeRequest): Promise<Employee> {

    const data = await apiRequest(`${backendUrl}/api/employees`, {
        method: "POST",
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
    });

    return data as Employee;
}

export async function deleteEmployee(request: DeleteEmployeeRequest, callback: DeleteEmployeeCallback): Promise<void> {

    const employeeId = request.employeeId;
    try {
        const response = await fetch(`${backendUrl}/api/employees/` + employeeId, {
            method: 'DELETE',
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        callback(employeeId);

    } catch (error) {
        console.error('There was a problem with the fetch operation:', error);
    }
}


export async function updateEmployeeAttributes(employeeCode: string, requestData: AttributeUpdate[]): Promise<AttributeValue[]> {

    const data = await apiRequest(`${backendUrl}/api/employees/${employeeCode}/attributes`, {
        method: "PATCH",
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData),
    });

    return data as AttributeValue[];
}

export async function updateEmployees(request: UpdateEmployeesRequest, callback: UpdateEmployeesResponseCallback): Promise<void> {

    try {
        const response = await fetch(`${backendUrl}/api/employees`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(request),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        const data = await response.json();
        callback(data);

    } catch (error) {
        console.error('There was a problem with the fetch operation:', error);
    }
}

export async function retrieveEmployeeUserAccount(employeeCode: string): Promise<UserAccount> {
    const data = await apiRequest(`${backendUrl}/api/employees/${employeeCode}/user-account`, {
        method: "GET",
    });

    return data as UserAccount;
}

export async function createEmployeeUserAccount(employeeId: string, request: EmployeeUserAccountCreateRequest): Promise<UserAccount> {
    const data = await apiRequest(`${backendUrl}/api/employees/${employeeId}/user-account`, {
        method: "POST",
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
    });

    return data as UserAccount;
}

export async function submitEmployeeAction(employeeCode: string, request: EmployeeEventRequest): Promise<BusinessAction> {
    const businessActionType = request.businessEventType;
    const data = await apiRequest(`${backendUrl}/api/employees/${employeeCode}/actions/${businessActionType}`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
    });
    return data as BusinessAction;
}

/**
 * Submits an undo for a specific applied business action.
 *
 * Both the action name (definition name) and the concrete action ID are required
 * by the backend endpoint: POST /{employee-code}/actions/{business-action}/{business-action-id}/undo
 */
export async function submitUndoEmployeeAction(
    employeeCode: string,
    businessActionName: string,
    businessActionId: string,
): Promise<BusinessAction> {
    const data = await apiRequest(
        `${backendUrl}/api/employees/${employeeCode}/actions/${businessActionName}/${businessActionId}/undo`,
        { method: "POST" },
    );
    return data as BusinessAction;
}

export interface CountEmployeesRequest {
    attributeFilters: AttributeFilter[] | undefined;
}

export interface CountEmployeesResult {
    count: number;
}

export interface ExportEmployeesRequest {
    attributeFilters: AttributeFilter[];
    employeeIds?: string[];
    columns: string[];
    format: 'CSV';
    // format: 'CSV' | 'XLSX';
}

// TODO: Can search be used here as well, but without selecting any column?
export const countEmployees = (request: CountEmployeesRequest): Promise<ApiResponse<CountEmployeesResult>> => {
    return apiClient.post('/count', request);
};

export const exportEmployees = (request: ExportEmployeesRequest): Promise<any> => {
    return apiClient.downloadFile('/export', request);
};

export const fetchAnnualLeaveBalanceForEmployee = (employeeId: string): Promise<ApiResponse<AnnualLeaveBalance>> => {
    return apiClient.get(`/${employeeId}/leave-balances`);
};